import React, { Fragment, useContext, useMemo } from 'react';
import { ascending, deviation, mean, median, merge } from 'd3-array';
import { timeMinute } from 'd3-time';
import { useTranslation } from 'react-i18next';

import NutritionSettingContext from './NutritionSettingsContext';
import PresentationContext from '../PresentationContext';
import Stats from './Stats';
import { timeSort } from '../utils/dataUtils';
import './Dashboard.css';

//const debug = process.env.NODE_ENV !== 'production';

function NutritionStats({ days, nutritionData: nutrition }) {
  const {
    mealThreshold,
  } = useContext(NutritionSettingContext);
  const { formatDateString, durationFormat } = useContext(PresentationContext);
  const { t } = useTranslation('dashboard');

  const lastDay = days[days.length - 1];

  const stats = useMemo(() => {
    const morningFilter = d => new Date(d.time).getHours() > 5;
    const snackFilter = d => d.carbohydrate >= mealThreshold;
      
    const intervals = Object.values(nutrition)
    .map(dayArray => dayArray
      .filter(d => (morningFilter(d) && snackFilter(d)))
      .sort(timeSort)
      .reduce((arr, c, i, a) => {
        if (i === 0) {
          return arr;
        }
        const previousTime = new Date(a[i - 1].time);
        const thisTime = new Date(c.time);
        arr.push(timeMinute.count(previousTime, thisTime));
        return arr;
      }, []));
    const allIntervals = merge(intervals).sort(ascending);
    const allCarbs = merge(Object.values(nutrition))
      .map(d => d.carbohydrate)
      .filter(d => d >= mealThreshold)
      .sort(ascending);

    const mealCounts = Object.values(nutrition).map(a => a.length);

    return {
      meals: {
        mean: mean(mealCounts) || 0,
        deviation: deviation(mealCounts) || 0,
      },
      carbs: {
        mean: mean(allCarbs) || 0,
        deviation: deviation(allCarbs) || 0,
      },
      intervals: {
        low: allIntervals[0] || 0,
        mean: mean(allIntervals) || 0,
        median: median(allIntervals) || 0,
        high: allIntervals[allIntervals.length - 1] || Math.max(0, (allIntervals[0] || 0)),
        deviation: deviation(allIntervals) || 0,
      },
    }
  }, [mealThreshold, nutrition]);

  return (
    <Stats id="stats" className="stats">
      <h2>{t('nutrition.heading')}</h2>
      <dl id="nutritionStats">
        <dt>
          <time dateTime={days[0]}>{formatDateString(days[0])}</time>
          {
            days.length > 1
            ? (
              <Fragment>
                <span> – </span>
                <time dateTime={lastDay}>{formatDateString(lastDay)}</time>
              </Fragment>
            )
            : null
          }
        </dt>
        <dd>
          <time dateTime={`PD${days.length}`}>{t('nutrition.timeRange', {count: days.length})}</time>
        </dd>
        <dt>{t('nutrition.stats.meals')}</dt>
        <dd>{t('nutrition.stats.mealStats', {
          mean: stats.meals.mean.toFixed(0),
          deviation: stats.meals.deviation.toFixed(0),
        })}</dd>
        <dt>{t('nutrition.stats.intervals')}</dt>
        <dd>{t('nutrition.stats.intervalStats', {
          mean: durationFormat(stats.intervals.mean),
          deviation: durationFormat(stats.intervals.deviation),
        })}</dd>
        <dt>{t('nutrition.stats.carbs')}</dt>
        <dd>{t('nutrition.stats.carbStats', {
          mean: stats.carbs.mean.toFixed(0),
          deviation:  stats.carbs.deviation.toFixed(0),
        })}</dd>
      </dl>
    </Stats>
  );
}

export default NutritionStats;
